// The physical RESTORE clinics, one entry for each clinic.
//
// Source: the Acuity booking calendars (owner 24213130), read on 3 Oct 2026.
// Where Acuity gives no suite or ZIP code, the value comes from
// restore-locations.ts for the same building. Geo comes from
// restore-locations.ts. St. Charles has no entry there, so it has no geo.
//
// `page` is the one page that represents the clinic. It is the
// /locations/... page when that page renders, otherwise the clinic booking
// page. Hancock Center and St. Charles have no page of their own (both
// /locations/ URLs redirect to the Chicago hub), so `page` is null.
//
// The JSON-LD on clinic pages, regional hubs and /llms.txt reads this list.
// Add or close a clinic here, not in a page file.

export type ClinicHub = "chicago" | "denver" | "dallas" | "charlotte" | "new-york";

export type Clinic = {
  slug: string;
  name: string;
  street: string;
  city: string;
  region: string;
  postalCode: string;
  geo?: { lat: number; lng: number };
  page: string | null;
  hub: ClinicHub;
};

export const CLINICS: Clinic[] = [
  {
    slug: "fulton-market",
    name: "Chicago Fulton Market",
    street: "159 N Sangamon St, Suite 200",
    city: "Chicago",
    region: "IL",
    postalCode: "60607",
    geo: { lat: 41.884982870906654, lng: -87.65075648876456 },
    page: "/schedule-a-consult/fulton-market/",
    hub: "chicago",
  },
  {
    slug: "hancock",
    name: "Chicago Hancock Center",
    street: "875 N Michigan Ave, 31st Floor",
    city: "Chicago",
    region: "IL",
    postalCode: "60611",
    geo: { lat: 41.898883, lng: -87.6242446 },
    page: null,
    hub: "chicago",
  },
  {
    slug: "lincoln-park",
    name: "Chicago Lincoln Park",
    street: "939 W. North Ave, Suite 750",
    city: "Chicago",
    region: "IL",
    postalCode: "60642",
    geo: { lat: 41.9102689, lng: -87.6520576 },
    page: "/schedule-a-consult/lincoln-park/",
    hub: "chicago",
  },
  {
    slug: "lake-forest",
    name: "Lake Forest",
    street: "100 S. Saunders Rd, Suite 150",
    city: "Lake Forest",
    region: "IL",
    postalCode: "60045",
    geo: { lat: 42.2385665, lng: -87.9010864 },
    page: "/schedule-a-consult/lake-forest/",
    hub: "chicago",
  },
  {
    slug: "oak-brook",
    name: "Oak Brook",
    street: "1415 W 22nd St, Ste 950",
    city: "Oak Brook",
    region: "IL",
    postalCode: "60523",
    geo: { lat: 41.849648, lng: -87.9526659 },
    page: "/schedule-a-consult/oak-brook/",
    hub: "chicago",
  },
  {
    slug: "schaumburg",
    name: "Schaumburg",
    street: "10 N Martingale Rd, #400",
    city: "Schaumburg",
    region: "IL",
    postalCode: "60173",
    geo: { lat: 42.0017067, lng: -88.2170257 },
    page: "/schedule-a-consult/schaumburg/",
    hub: "chicago",
  },
  {
    slug: "st-charles",
    name: "St. Charles",
    street: "100 Illinois St, Ste 100",
    city: "St. Charles",
    region: "IL",
    postalCode: "60174",
    page: null,
    hub: "chicago",
  },
  {
    slug: "boulder",
    name: "Boulder",
    street: "4770 Baseline Rd, Office 232",
    city: "Boulder",
    region: "CO",
    postalCode: "80303",
    geo: { lat: 40.0044495, lng: -105.2753998 },
    page: "/locations/colorado/boulder/",
    hub: "denver",
  },
  {
    slug: "denver",
    name: "Downtown Denver",
    street: "2301 Blake St, Office 113",
    city: "Denver",
    region: "CO",
    postalCode: "80205",
    geo: { lat: 39.7575248, lng: -104.9906841 },
    page: "/schedule-a-consult/denver/",
    hub: "denver",
  },
  {
    slug: "greenwood-village",
    name: "Greenwood Village",
    street: "5445 DTC Pkwy, Suite 1015",
    city: "Greenwood Village",
    region: "CO",
    postalCode: "80111",
    geo: { lat: 39.6184617, lng: -104.8994017 },
    page: "/schedule-a-consult/greenwood-village/",
    hub: "denver",
  },
  {
    slug: "charlotte",
    name: "Charlotte",
    street: "7725 Ballantyne Commons Pkwy, Suite 101",
    city: "Charlotte",
    region: "NC",
    postalCode: "28277",
    geo: { lat: 35.05877271581308, lng: -80.80896961534265 },
    page: "/schedule-a-consult/charlotte/",
    hub: "charlotte",
  },
  {
    slug: "new-york",
    name: "New York",
    street: "240 W 37th St, 5th Floor",
    city: "New York",
    region: "NY",
    postalCode: "10018",
    geo: { lat: 40.7528559, lng: -73.9933142 },
    page: "/schedule-a-consult/manhattan/",
    hub: "new-york",
  },
  {
    slug: "brooklyn",
    name: "Brooklyn",
    street: "300 Cadman Plaza W, 12th Floor",
    city: "Brooklyn",
    region: "NY",
    postalCode: "11201",
    geo: { lat: 40.6950891, lng: -73.9938434 },
    page: "/locations/new-york/brooklyn-heights/",
    hub: "new-york",
  },
  {
    slug: "plano",
    name: "Plano",
    street: "5425 W. Spring Creek Pkwy, Suite 170",
    city: "Plano",
    region: "TX",
    postalCode: "75024",
    geo: { lat: 33.06329425281087, lng: -96.81124550376684 },
    page: "/schedule-a-consult/plano/",
    hub: "dallas",
  },
  {
    slug: "highland-park-place",
    name: "Dallas Highland Park Place",
    street: "4514 Cole Ave, Suite 600",
    city: "Dallas",
    region: "TX",
    postalCode: "75205",
    geo: { lat: 32.8218715, lng: -96.7914296 },
    page: "/locations/texas/dallas-highland-park/",
    hub: "dallas",
  },
  {
    slug: "southlake",
    name: "Southlake",
    street: "950 State Highway 114, Suite 160",
    city: "Southlake",
    region: "TX",
    postalCode: "76092",
    geo: { lat: 32.95544816530427, lng: -97.13579467116433 },
    page: "/locations/texas/south-lake/",
    hub: "dallas",
  },
];

/** The market hub pages, and the clinics each one lists. */
export const CLINIC_HUBS: { hub: ClinicHub; name: string; page: string }[] = [
  { hub: "chicago", name: "Chicago, IL", page: "/locations/restore-chicago/" },
  { hub: "denver", name: "Denver, CO", page: "/locations/restore-denver/" },
  { hub: "dallas", name: "Dallas, TX", page: "/locations/restore-dallas/" },
  { hub: "charlotte", name: "Charlotte, NC", page: "/locations/restore-charlotte/" },
  { hub: "new-york", name: "New York, NY", page: "/locations/new-york/" },
];

export function clinicsIn(...hubs: ClinicHub[]): Clinic[] {
  return CLINICS.filter((c) => hubs.includes(c.hub));
}

export function clinicBySlug(slug: string): Clinic {
  const clinic = CLINICS.find((c) => c.slug === slug);
  if (!clinic) throw new Error(`Unknown clinic: ${slug}`);
  return clinic;
}

export function clinicAddressLine(c: Clinic): string {
  return `${c.street}, ${c.city}, ${c.region} ${c.postalCode}`;
}
